import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

/**
 * Component for handling governance proposal management
 */
const ProposalManager = ({
  governanceContract,
  fundContract,
  yildxTokenContract,
  account,
  setError,
  setSuccess,
  setLoading,
  loading,
  proposalRedirect,
  setProposalRedirect,
  canPropose,
  currentOperator,
  showOperatorManagement,
  formatAddress,
  setTxStatus,
  setTxHash,
  setTxMessage,
  setShowTxPopup
}) => {
  // State for proposals
  const [proposals, setProposals] = useState([]);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalDetails, setShowProposalDetails] = useState(false);
  
  // State for proposal form
  const [proposalType, setProposalType] = useState(0); // Default to managementFee
  const [proposalDescription, setProposalDescription] = useState('');
  const [proposalData, setProposalData] = useState({
    managementFee: 200, // Default 2%
    entryFee: 25,      // Default 0.25%
    exitFee: 25,       // Default 0.25%
    slippageTolerance: 300, // Default 3%
    tokenAddress: '',
    tokenAllocation: '',
    tokenPriceFeed: '',
    operatorAddress: '' // Added for operator management
  });

  // Add state for the auto-approval status
  const [autoApprovalStatuses, setAutoApprovalStatuses] = useState({});
  
  // Helper function to safely convert blockchain timestamp to JS Date
  const blockchainTimeToDate = (timestamp) => {
    // If it's already a Date object, return it
    if (timestamp instanceof Date) return timestamp;
    
    // If it's a BigNumber, convert to number
    if (timestamp._isBigNumber) {
      timestamp = timestamp.toNumber();
    }
    
    // Ensure timestamp is treated as seconds (blockchain standard)
    // and convert to milliseconds for JS Date
    return new Date(timestamp * 1000);
  };
  
  /**
   * Safely converts BigNumber to a JavaScript number without overflow errors
   * For large numbers, it returns the formatted string instead
   */
  const safeNumberConversion = (bigNumber) => {
    if (!bigNumber) return 0;
    
    try {
      // For small numbers, convert to actual number
      return bigNumber.toNumber();
    } catch (error) {
      if (error.code === "NUMERIC_FAULT" && error.fault === "overflow") {
        // For large numbers that would overflow, return formatted string
        return ethers.utils.formatEther(bigNumber);
      }
      throw error;
    }
  };
  
  // Add a useEffect to handle the redirected proposal
  useEffect(() => {
    if (proposalRedirect) {
      // Pre-fill the proposal form based on the redirected data
      setProposalType(proposalRedirect.type);
      setProposalDescription(proposalRedirect.description);
      
      // Handle different proposal data based on type
      if (proposalRedirect.params) {
        // Set appropriate proposal data based on type
        setProposalData(prevData => ({
          ...prevData,
          ...proposalRedirect.params
        }));
      }
      
      // Clear the redirect after it's been handled
      if (setProposalRedirect) {
        setProposalRedirect(null);
      }
      
      // Show a success message to the user
      setSuccess("Proposal data pre-filled from Token Management. Review and create your proposal.");
    }
  }, [proposalRedirect, setProposalRedirect, setSuccess]);
  
  // Update operator address when currentOperator changes
  useEffect(() => {
    if (currentOperator && currentOperator.address) {
      setProposalData(prevData => ({
        ...prevData,
        operatorAddress: currentOperator.address
      }));
    }
  }, [currentOperator]);

  // Load proposals
  useEffect(() => {
    if (governanceContract && account) {
      loadProposals();
    }
  }, [governanceContract, account]);
  
  // Reset state before loading proposals
  const resetState = () => {
    setProposals([]);
    setAutoApprovalStatuses({});
  };
  
  // Comprehensive reload function with delays to ensure state is updated
  const reloadProposals = async () => {
    // Reset all proposal-related state
    resetState();
    
    // Show loading indicator
    setLoading(true);
    
    // Small delay to ensure any blockchain state changes are visible
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Load proposals
    await loadProposals();
    
    // Hide loading indicator
    setLoading(false);
  };
  
  // Load all active proposals
  const loadProposals = async () => {
    try {
      setLoading(true);
      
      // Get proposal count
      const proposalCount = await governanceContract.proposalCount();
      console.log(`Found ${proposalCount} proposals`);
      
      const proposalList = [];
      const newAutoApprovalStatuses = {};
      
      // Get current date for comparison
      const currentDate = new Date();
      
      // Load each proposal
      for (let i = 0; i < proposalCount; i++) {
        try {
          const proposal = await governanceContract.getProposal(i);
          
          // Convert blockchain timestamps to JS Dates
          const creationTime = blockchainTimeToDate(proposal.creationTime);
          const votingEndTime = blockchainTimeToDate(proposal.votingEndTime);
          
          // Get user vote status
          const [hasVoted, support, weight] = await governanceContract.hasVoted(i, account);
          
          // Get auto-approval status
          let autoApprovalStatus = { canAutoApprove: false };
          let strongMajorityStatus = { hasStrongMajority: false };
          
          try {
            autoApprovalStatus = await governanceContract.checkAutoApprovalStatus(i);
            
            // Try to get strong majority status if available
            try {
              strongMajorityStatus = await governanceContract.checkStrongMajorityStatus(i);
            } catch (smError) {
              console.log(`Strong majority check not available for proposal ${i}:`, smError.message);
            }
            
            // Store the combined status - avoid toNumber() for large numbers
            newAutoApprovalStatuses[i] = {
              canAutoApprove: autoApprovalStatus.canAutoApprove,
              requiredVotes: autoApprovalStatus.requiredVotes, // Keep as BigNumber
              currentVotes: autoApprovalStatus.currentVotes,   // Keep as BigNumber
              threshold: safeNumberConversion(autoApprovalStatus.threshold),
              hasStrongMajority: strongMajorityStatus.hasStrongMajority || false,
              approvalPercentage: safeNumberConversion(strongMajorityStatus.approvalPercentage)
            };
          } catch (statusError) {
            console.error(`Error checking auto-approval status for proposal ${i}:`, statusError.message);
          }
          
          // Add to proposal list - keeping BigNumber values as is
          proposalList.push({
            id: i,
            proposer: proposal.proposer,
            proposalType: proposal.proposalType,
            description: proposal.description,
            creationTime,
            votingEndTime,
            votesFor: proposal.votesFor,
            votesAgainst: proposal.votesAgainst,
            executed: proposal.executed,
            canceled: proposal.canceled,
            canExecute: proposal.canExecute,
            // User-specific data
            hasVoted,
            userSupport: support,
            userWeight: weight, // Keep as BigNumber
            // Auto-approval data
            canAutoApprove: autoApprovalStatus.canAutoApprove,
            hasStrongMajority: strongMajorityStatus.hasStrongMajority || false
          });
        } catch (error) {
          console.error(`Error loading proposal ${i}:`, error);
        }
      }
      
      console.log('Loaded proposals:', proposalList);
      setProposals(proposalList);
      setAutoApprovalStatuses(newAutoApprovalStatuses);
      
    } catch (error) {
      console.error('Error loading proposals:', error);
      setError('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };
  
  // Create a new proposal
  const handleCreateProposal = async (explicitType = null) => {
    if (!proposalDescription) {
      setError('Please enter a proposal description');
      return;
    }
    
    if (!canPropose) {
      setError('You need at least 100 YILDX tokens to create proposals');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Use the explicit type if provided, otherwise use the state value
      const typeToUse = explicitType !== null ? explicitType : Number(proposalType);
      let callData;
      
      switch (typeToUse) {
        case 0: // ChangeManagementFee
          callData = await governanceContract.getSetManagementFeeCallData(proposalData.managementFee);
          break;
        case 1: // ChangeEntryFee
          callData = await governanceContract.getSetEntryFeeCallData(proposalData.entryFee);
          break;
        case 2: // ChangeExitFee
          callData = await governanceContract.getSetExitFeeCallData(proposalData.exitFee);
          break;
        case 3: // AddToken
          if (!proposalData.tokenAddress || !proposalData.tokenAllocation || !proposalData.tokenPriceFeed) {
            throw new Error('Token details missing');
          }
          callData = await governanceContract.getAddTokenCallData(
            proposalData.tokenAddress,
            proposalData.tokenAllocation,
            proposalData.tokenPriceFeed
          );
          break;
        case 4: // RemoveToken
          if (!proposalData.tokenAddress) {
            throw new Error('Token address missing');
          }
          callData = await governanceContract.getRemoveTokenCallData(proposalData.tokenAddress);
          break;
        case 5: // UpdateTokenAllocation
          if (!proposalData.tokenAddress || !proposalData.tokenAllocation) {
            throw new Error('Token details missing');
          }
          callData = await governanceContract.getUpdateTokenAllocationCallData(
            proposalData.tokenAddress,
            proposalData.tokenAllocation
          );
          break;
        case 6: // ChangeSlippageTolerance
          callData = await governanceContract.getSetSlippageToleranceCallData(proposalData.slippageTolerance);
          break;
        case 7: // AddOperator
          if (!proposalData.operatorAddress || !ethers.utils.isAddress(proposalData.operatorAddress)) {
            throw new Error('Valid operator address required');
          }
          callData = await governanceContract.getAddOperatorCallData(proposalData.operatorAddress);
          break;
        case 8: // RemoveOperator
          if (!proposalData.operatorAddress || !ethers.utils.isAddress(proposalData.operatorAddress)) {
            throw new Error('Valid operator address required');
          }
          callData = await governanceContract.getRemoveOperatorCallData(proposalData.operatorAddress);
          break;
        default:
          throw new Error('Invalid proposal type');
      }
      
      // Create the proposal
      const tx = await governanceContract.createProposal(
        typeToUse,
        proposalDescription,
        callData
      );
      
      await tx.wait();
      
      setSuccess('Proposal created successfully');
      
      // Reset form
      setProposalDescription('');
      
      // Refresh proposals
      await reloadProposals();
      
    } catch (error) {
      console.error('Error creating proposal:', error);
      setError(error.message || 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };
  
  // Vote on a proposal
  const handleVote = async (proposalId, support) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Your vote (${support ? 'For' : 'Against'}) is being processed on the blockchain.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.castVote(proposalId, support);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully voted ${support ? 'for' : 'against'} proposal #${proposalId}`);
      setSuccess(`Successfully voted ${support ? 'for' : 'against'} proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
    } catch (error) {
      console.error(`Error voting on proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to vote: ${error.message}`);
        setError(error.message || 'Failed to vote on proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Execute a proposal
  const handleExecuteProposal = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Executing proposal #${proposalId}. This transaction is being processed on the blockchain.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.executeProposal(proposalId);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully executed proposal #${proposalId}`);
      setSuccess(`Successfully executed proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
      
      // Close detail modal if open
      if (showProposalDetails && selectedProposal && selectedProposal.id === proposalId) {
        setShowProposalDetails(false);
      }
    } catch (error) {
      console.error(`Error executing proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to execute proposal: ${error.message}`);
        setError(error.message || 'Failed to execute proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Manually trigger auto-approval check
  const handleTriggerAutoApproval = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Triggering auto-approval check for proposal #${proposalId}.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.triggerAutoApprovalCheck(proposalId);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Auto-approval check triggered for proposal #${proposalId}`);
      setSuccess(`Auto-approval check triggered for proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
    } catch (error) {
      console.error(`Error triggering auto-approval for proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to trigger auto-approval check: ${error.message}`);
        setError(error.message || 'Failed to trigger auto-approval check');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to bypass timelock
  const handleBypassTimelock = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Bypassing timelock for proposal #${proposalId}.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.bypassTimelock(proposalId);
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully bypassed timelock for proposal #${proposalId}`);
      setSuccess(`Successfully bypassed timelock for proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
      
      // Close detail modal if open
      if (showProposalDetails && selectedProposal && selectedProposal.id === proposalId) {
        setShowProposalDetails(false);
      }
    } catch (error) {
      console.error(`Error bypassing timelock for proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to bypass timelock: ${error.message}`);
        setError(error.message || 'Failed to bypass timelock');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel a proposal
  const handleCancelProposal = async (proposalId) => {
    setLoading(true);
    
    try {
      // Reset transaction popup state
      setTxStatus('pending');
      setTxHash('');
      setTxMessage(`Cancelling proposal #${proposalId}.`);
      setShowTxPopup(true);
      
      const tx = await governanceContract.cancelProposal(proposalId, "Cancelled by proposer");
      setTxHash(tx.hash);
      
      await tx.wait();
      
      // Update status
      setTxStatus('success');
      setTxMessage(`Successfully canceled proposal #${proposalId}`);
      setSuccess(`Successfully canceled proposal #${proposalId}`);
      
      // Use enhanced reload function
      await reloadProposals();
      
      // Close detail modal if open
      if (showProposalDetails && selectedProposal && selectedProposal.id === proposalId) {
        setShowProposalDetails(false);
      }
    } catch (error) {
      console.error(`Error canceling proposal #${proposalId}:`, error);
      
      // Check if the error is due to user rejecting the transaction
      if (error.code === 4001 || (error.message && error.message.includes('user rejected'))) {
        setTxStatus('cancelled');
        setTxMessage('You cancelled the transaction.');
      } else {
        setTxStatus('failed');
        setTxMessage(`Failed to cancel proposal: ${error.message}`);
        setError(error.message || 'Failed to cancel proposal');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Format a timestamp for display
  const formatTime = (date) => {
    return date.toLocaleString();
  };
  
  // Get proposal type as string
  const getProposalTypeString = (type) => {
    const types = [
      'Change Management Fee',
      'Change Entry Fee',
      'Change Exit Fee',
      'Add Token',
      'Remove Token',
      'Update Token Allocation',
      'Change Slippage Tolerance',
      'Add Operator',
      'Remove Operator',
      'Emergency Action',
      'Misc Admin Action'
    ];
    
    return types[type] || 'Unknown';
  };
  
  // Show proposal details modal
  const showProposalModal = (proposal) => {
    setSelectedProposal(proposal);
    setShowProposalDetails(true);
  };
  
  // Hide proposal details modal
  const hideProposalModal = () => {
    setShowProposalDetails(false);
    setSelectedProposal(null);
  };
  
  // Render the votes display component - updated to handle BigNumber
  const renderVotesDisplay = (proposal) => {
    // Convert BigNumber to string values using ethers.utils.formatEther
    const votesFor = ethers.utils.formatEther(proposal.votesFor);
    const votesAgainst = ethers.utils.formatEther(proposal.votesAgainst);
    
    // Calculate total and percentage
    const totalVotes = parseFloat(votesFor) + parseFloat(votesAgainst);
    const forPercentage = totalVotes > 0 ? (parseFloat(votesFor) / totalVotes) * 100 : 0;
    
    return (
      <div className="flex items-center">
        <span className="text-green-600 mr-2">{votesFor}</span> / 
        <span className="text-red-600 ml-2">{votesAgainst}</span>
      </div>
    );
  };
  
  // Auto-Approval Status Component - Updated to handle BigNumber
  const AutoApprovalStatus = ({ proposalId }) => {
    const status = autoApprovalStatuses[proposalId];
    
    if (!status) {
      return <div className="text-gray-500">Loading approval status...</div>;
    }
    
    return (
      <div className="space-y-1">
        <div className="flex justify-between">
          <span>Can auto-approve:</span> 
          <span className={status.canAutoApprove ? "text-green-600 font-medium" : "text-red-600"}>
            {status.canAutoApprove ? "Yes" : "No"}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span>Current votes:</span> 
          <span>
            {status.currentVotes && ethers.utils.formatEther(status.currentVotes)} / 
            {status.requiredVotes && ethers.utils.formatEther(status.requiredVotes)} required 
            ({status.threshold}%)
          </span>
        </div>
        
        {/* Show strong majority status if available */}
        <div className="flex justify-between">
          <span>Strong majority status:</span>
          <span className={status.hasStrongMajority ? "text-green-600 font-medium" : "text-gray-600"}>
            {status.hasStrongMajority 
              ? "Eligible for immediate execution" 
              : `${status.approvalPercentage}% (below threshold)`}
          </span>
        </div>
      </div>
    );
  };
  
  return (
    <>
      {/* Operator Management Interface */}
      {showOperatorManagement && (
        <div className="space-y-4 mt-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Add New Operator Section */}
            <div className="p-4 bg-white rounded-lg shadow-sm border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-3">Propose New Operator</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Operator Address
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md"
                    placeholder="0x..."
                    value={proposalData.operatorAddress}
                    onChange={(e) => setProposalData({...proposalData, operatorAddress: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proposal Description
                  </label>
                  <textarea
                    className="w-full p-2 border rounded-md"
                    rows="2"
                    placeholder="Explain why this address should be the new operator"
                    value={proposalDescription}
                    onChange={(e) => setProposalDescription(e.target.value)}
                  ></textarea>
                </div>
                <button
                  className="w-full bg-amber-600 text-white py-2 px-4 rounded-md hover:bg-amber-700 disabled:bg-gray-400"
                  onClick={() => handleCreateProposal(7)} // Pass explicit type
                  disabled={loading || !canPropose || !proposalData.operatorAddress || !proposalDescription}
                >
                  {loading ? 'Creating...' : 'Create Proposal to Add Operator'}
                </button>
              </div>
            </div>
            
            {/* Remove Current Operator Section */}
            {currentOperator && (
              <div className="p-4 bg-white rounded-lg shadow-sm border border-red-200">
                <h4 className="font-medium text-red-800 mb-3">Propose Operator Removal</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Operator Address
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md bg-gray-50"
                      value={currentOperator.address}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proposal Description
                    </label>
                    <textarea
                      className="w-full p-2 border rounded-md"
                      rows="2"
                      placeholder="Explain why the current operator should be removed"
                      value={proposalDescription}
                      onChange={(e) => setProposalDescription(e.target.value)}
                    ></textarea>
                  </div>
                  <button
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                    onClick={() => {
                      setProposalData({...proposalData, operatorAddress: currentOperator.address});
                      handleCreateProposal(8); // Pass explicit type
                    }}
                    disabled={loading || !canPropose || !proposalDescription}
                  >
                    {loading ? 'Creating...' : 'Create Proposal to Remove Operator'}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Governance Explanation */}
          <div className="p-3 bg-amber-100 rounded-md text-sm text-amber-800">
            <p className="font-medium mb-1">About Operator Management:</p>
            <p>Operators have special permissions to manage the fund, including rebalancing the portfolio and executing certain administrative functions. Adding or removing operators requires a governance vote.</p>
            <p className="mt-1">These proposals require a majority vote to pass and can only be executed after the voting period ends, unless they achieve a strong majority.</p>
          </div>
        </div>
      )}
      
      {/* Proposal List */}
      <div className="mb-6">
        <h3 className="font-medium mb-4">Active Proposals</h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Votes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {proposals.length > 0 ? (
                proposals.map(proposal => (
                  <tr key={proposal.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => showProposalModal(proposal)}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">{proposal.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">{getProposalTypeString(proposal.proposalType)}</td>
                    <td className="px-4 py-3 text-sm">{proposal.description.substring(0, 50)}{proposal.description.length > 50 ? '...' : ''}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {renderVotesDisplay(proposal)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {proposal.executed ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Executed</span>
                      ) : proposal.canceled ? (
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">Canceled</span>
                      ) : new Date() > proposal.votingEndTime ? (
                        proposal.canExecute ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">Ready to Execute</span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Voting Ended</span>
                        )
                      ) : (
                        // Enhanced status display with strong majority badge
                        proposal.hasStrongMajority ? (
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Strong Majority
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">Voting</span>
                        )
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          showProposalModal(proposal);
                        }}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No proposals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            onClick={reloadProposals}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Proposals"}
          </button>
        </div>
      </div>
      
      {/* Create Proposal Form - Only show if user has enough tokens */}
      {canPropose && (
        <div className="border rounded-lg p-4 mb-6">
          <h3 className="font-medium mb-3">Create New Proposal</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proposal Type
              </label>
              <select
                className="w-full p-2 border rounded-md"
                value={proposalType}
                onChange={(e) => setProposalType(e.target.value)}
                disabled={loading}
              >
                <option value="0">Change Management Fee</option>
                <option value="1">Change Entry Fee</option>
                <option value="2">Change Exit Fee</option>
                <option value="3">Add Token</option>
                <option value="4">Remove Token</option>
                <option value="5">Update Token Allocation</option>
                <option value="6">Change Slippage Tolerance</option>
                <option value="7">Add Operator</option>
                <option value="8">Remove Operator</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-md"
                value={proposalDescription}
                onChange={(e) => setProposalDescription(e.target.value)}
                placeholder="Brief description of your proposal"
                disabled={loading}
              />
            </div>
            
            {/* Parameters based on proposal type */}
            {proposalType === "0" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Management Fee (in basis points, 100 = 1%)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.managementFee}
                  onChange={(e) => setProposalData({...proposalData, managementFee: parseInt(e.target.value)})}
                  placeholder="200"
                  min="0"
                  max="500"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Current: {proposalData.managementFee / 100}%, Max: 5%</p>
              </div>
            )}
            
            {proposalType === "1" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entry Fee (in basis points, 100 = 1%)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.entryFee}
                  onChange={(e) => setProposalData({...proposalData, entryFee: parseInt(e.target.value)})}
                  placeholder="25"
                  min="0"
                  max="100"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Current: {proposalData.entryFee / 100}%, Max: 1%</p>
              </div>
            )}
            
            {proposalType === "2" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exit Fee (in basis points, 100 = 1%)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.exitFee}
                  onChange={(e) => setProposalData({...proposalData, exitFee: parseInt(e.target.value)})}
                  placeholder="25"
                  min="0"
                  max="100"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Current: {proposalData.exitFee / 100}%, Max: 1%</p>
              </div>
            )}
            
            {proposalType === "3" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token Address
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md"
                    value={proposalData.tokenAddress}
                    onChange={(e) => setProposalData({...proposalData, tokenAddress: e.target.value})}
                    placeholder="0x..."
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allocation (in basis points, 100 = 1%)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={proposalData.tokenAllocation}
                    onChange={(e) => setProposalData({...proposalData, tokenAllocation: parseInt(e.target.value)})}
                    placeholder="1000"
                    min="0"
                    max="10000"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price Feed Address
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md"
                    value={proposalData.tokenPriceFeed}
                    onChange={(e) => setProposalData({...proposalData, tokenPriceFeed: e.target.value})}
                    placeholder="0x..."
                    disabled={loading}
                  />
                </div>
              </>
            )}
            
            {proposalType === "4" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Token Address to Remove
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.tokenAddress}
                  onChange={(e) => setProposalData({...proposalData, tokenAddress: e.target.value})}
                  placeholder="0x..."
                  disabled={loading}
                />
              </div>
            )}
            
            {proposalType === "5" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token Address
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md"
                    value={proposalData.tokenAddress}
                    onChange={(e) => setProposalData({...proposalData, tokenAddress: e.target.value})}
                    placeholder="0x..."
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Allocation (in basis points, 100 = 1%)
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={proposalData.tokenAllocation}
                    onChange={(e) => setProposalData({...proposalData, tokenAllocation: parseInt(e.target.value)})}
                    placeholder="1000"
                    min="0"
                    max="10000"
                    disabled={loading}
                  />
                </div>
              </>
            )}
            
            {proposalType === "6" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slippage Tolerance (in basis points, 100 = 1%)
                </label>
                <input
                  type="number"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.slippageTolerance}
                  onChange={(e) => setProposalData({...proposalData, slippageTolerance: parseInt(e.target.value)})}
                  placeholder="300"
                  min="0"
                  max="500"
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">Current: {proposalData.slippageTolerance / 100}%, Max: 5%</p>
              </div>
            )}
            
            {proposalType === "7" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Operator Address
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.operatorAddress}
                  onChange={(e) => setProposalData({...proposalData, operatorAddress: e.target.value})}
                  placeholder="0x..."
                  disabled={loading}
                />
                <p className="text-sm text-gray-500 mt-1">
                  The address that will be added as a new operator
                </p>
              </div>
            )}
            
            {proposalType === "8" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Operator Address to Remove
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md"
                  value={proposalData.operatorAddress}
                  onChange={(e) => setProposalData({...proposalData, operatorAddress: e.target.value})}
                  placeholder="0x..."
                  disabled={loading}
                />
                {currentOperator && (
                  <p className="text-sm text-gray-500 mt-1">
                    Current operator: {formatAddress(currentOperator.address)}
                  </p>
                )}
              </div>
            )}
            
            <button
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              onClick={() => handleCreateProposal()}
              disabled={loading || !proposalDescription}
            >
              {loading ? "Creating..." : "Create Proposal"}
            </button>
          </div>
        </div>
      )}
      
      {/* Proposal Details Modal */}
      {showProposalDetails && selectedProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full">
            <h3 className="text-xl font-bold mb-4">Proposal #{selectedProposal.id}</h3>
            
            <div className="space-y-3 mb-4">
              <div>
                <span className="font-medium">Type:</span> {getProposalTypeString(selectedProposal.proposalType)}
              </div>
              <div>
                <span className="font-medium">Description:</span> {selectedProposal.description}
              </div>
              <div>
                <span className="font-medium">Proposer:</span> {selectedProposal.proposer}
              </div>
              <div>
                <span className="font-medium">Created:</span> {formatTime(selectedProposal.creationTime)}
              </div>
              <div>
                <span className="font-medium">Voting Ends:</span> {formatTime(selectedProposal.votingEndTime)}
              </div>
              <div>
                <span className="font-medium">Status:</span> {
                  selectedProposal.executed ? "Executed" :
                  selectedProposal.canceled ? "Canceled" :
                  new Date() > selectedProposal.votingEndTime ? (
                    selectedProposal.canExecute ? "Ready to Execute" : "Voting Ended"
                  ) : selectedProposal.hasStrongMajority ? "Strong Majority Reached" : "Voting Active"
                }
              </div>
              <div>
                <span className="font-medium">Votes For:</span> {ethers.utils.formatEther(selectedProposal.votesFor)}
              </div>
              <div>
                <span className="font-medium">Votes Against:</span> {ethers.utils.formatEther(selectedProposal.votesAgainst)}
              </div>
              <div>
                <span className="font-medium">Your Vote:</span> {
                  selectedProposal.hasVoted ? 
                    (selectedProposal.userSupport ? "Voted For" : "Voted Against") : 
                    "Not Voted"
                }
              </div>
            </div>
            
            {/* Add Auto-Approval Status Section */}
            {!selectedProposal.executed && !selectedProposal.canceled && (
              <div className="mb-4 p-3 bg-indigo-50 rounded-md border border-indigo-100">
                <h4 className="text-sm font-semibold text-indigo-800 mb-2">Auto-Approval Status</h4>
                <AutoApprovalStatus proposalId={selectedProposal.id} />
                
                {/* Action Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                  {/* Trigger Auto-Approval Button */}
                  <button
                    className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
                    onClick={() => handleTriggerAutoApproval(selectedProposal.id)}
                    disabled={loading || selectedProposal.executed || selectedProposal.canceled}
                  >
                    {loading ? "Processing..." : "Trigger Auto-Approval Check"}
                  </button>
                  
                  {/* Bypass Timelock Button (only for admin) */}
                  <button
                    className="px-3 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 disabled:bg-gray-400"
                    onClick={() => handleBypassTimelock(selectedProposal.id)}
                    disabled={loading || selectedProposal.executed || selectedProposal.canceled}
                  >
                    {loading ? "Processing..." : "Bypass Timelock (Admin)"}
                  </button>
                </div>
                
                <p className="text-xs text-indigo-600 mt-1">
                  The auto-approval check verifies if the proposal has enough votes for automatic execution.
                  Bypass timelock is an admin-only function that can execute a proposal immediately if it has a strong majority.
                </p>
              </div>
            )}
            
            <div className="flex justify-between">
              <div>
                {/* Voting buttons */}
                {!selectedProposal.executed && !selectedProposal.canceled && new Date() <= selectedProposal.votingEndTime && !selectedProposal.hasVoted && (
                  <div className="space-x-2">
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-gray-400"
                      onClick={() => {
                        handleVote(selectedProposal.id, true);
                        hideProposalModal();
                      }}
                      disabled={loading}
                    >
                      Vote For
                    </button>
                    <button
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                      onClick={() => {
                        handleVote(selectedProposal.id, false);
                        hideProposalModal();
                      }}
                      disabled={loading}
                    >
                      Vote Against
                    </button>
                  </div>
                )}
                
                {/* Proposer cancel button */}
                {!selectedProposal.executed && !selectedProposal.canceled && (account.toLowerCase() === selectedProposal.proposer.toLowerCase()) && (
                  <button
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-gray-400"
                    onClick={() => {
                      handleCancelProposal(selectedProposal.id);
                      hideProposalModal();
                    }}
                    disabled={loading}
                  >
                    Cancel Proposal
                  </button>
                )}
              </div>
              
              <div className="space-x-2">
                {/* Execute button */}
                {selectedProposal.canExecute && (
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                    onClick={() => {
                      handleExecuteProposal(selectedProposal.id);
                      hideProposalModal();
                    }}
                    disabled={loading}
                  >
                    Execute Proposal
                  </button>
                )}
                
                <button
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-400"
                  onClick={hideProposalModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProposalManager;